import { Fragment, useMemo, type ReactNode } from "react";
import { cn } from "@rekati/utils";
import { parseMarkdown, type Inline } from "./markdown";

function inline(nodes: Inline[]): ReactNode {
  return nodes.map((n, i) => {
    if (n.type === "text") return <Fragment key={i}>{n.text}</Fragment>;
    if (n.type === "code")
      return (
        <code key={i} className="rounded bg-background/70 px-1 py-0.5 font-mono text-[0.85em]">
          {n.text}
        </code>
      );
    if (n.type === "strong")
      return (
        <strong key={i} className="font-semibold">
          {inline(n.children)}
        </strong>
      );
    return <em key={i}>{inline(n.children)}</em>;
  });
}

/** One of Kara's answers, its markdown rendered as React nodes (see markdown.ts). */
export function KaraText({ text, className }: { text: string; className?: string }) {
  const blocks = useMemo(() => parseMarkdown(text), [text]);
  return (
    <div className={cn("grid gap-2", className)}>
      {blocks.map((b, i) => {
        switch (b.type) {
          case "p":
            return (
              <p key={i}>
                {b.lines.map((l, j) => (
                  <Fragment key={j}>
                    {j ? <br /> : null}
                    {inline(l)}
                  </Fragment>
                ))}
              </p>
            );
          case "h":
            return (
              <p key={i} className="font-semibold">
                {inline(b.children)}
              </p>
            );
          case "ul":
            return (
              <ul key={i} className="grid list-disc gap-1 pl-5 marker:text-muted-foreground">
                {b.items.map((it, j) => (
                  <li key={j}>{inline(it)}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol
                key={i}
                start={b.start}
                className="grid list-decimal gap-1 pl-5 marker:text-muted-foreground"
              >
                {b.items.map((it, j) => (
                  <li key={j}>{inline(it)}</li>
                ))}
              </ol>
            );
          case "table":
            return (
              <div key={i} className="overflow-x-auto rounded-lg border border-border bg-background/50">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      {b.head.map((c, j) => (
                        <th key={j} className="px-2.5 py-1.5 font-semibold whitespace-nowrap">
                          {inline(c)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((r, j) => (
                      <tr key={j} className="border-b border-border last:border-0">
                        {r.map((c, k) => (
                          <td key={k} className="px-2.5 py-1.5 tabular-nums">
                            {inline(c)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "code":
            return (
              <pre
                key={i}
                className="overflow-x-auto rounded-lg bg-background/70 px-2.5 py-2 font-mono text-xs whitespace-pre"
              >
                {b.text}
              </pre>
            );
        }
      })}
    </div>
  );
}
